import React from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    TouchSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    useSortable,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

/**
 * Generic drag-and-drop list. Long-press on mobile, click-drag on desktop.
 * Caller supplies `items` (must have a stable `.id`) and `onReorder(items)`
 * called whenever the order changes. The `renderItem(item, index)` returns
 * the visible row contents (the grip handle is added automatically).
 */
export default function SortableSlots({ items, onReorder, renderItem, disabled = false }) {
    const sensors = useSensors(
        // Touch: 250ms long-press defeats accidental scroll capture.
        useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 6 } }),
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = items.findIndex((s) => s.id === active.id);
        const newIndex = items.findIndex((s) => s.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        onReorder(arrayMove(items, oldIndex, newIndex));
    };

    if (disabled) {
        return (
            <div className="space-y-3" data-testid="sortable-disabled">
                {items.map((item, i) => (
                    <div
                        key={item.id}
                        data-testid={`locked-row-${item.id}`}
                        className="rounded-sm border border-border bg-card p-3"
                    >
                        {renderItem(item, i, { disabled: true })}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3" data-testid="sortable-list">
                    {items.map((item, i) => (
                        <SortableRow key={item.id} id={item.id}>
                            {renderItem(item, i)}
                        </SortableRow>
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}

function SortableRow({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.85 : 1,
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            data-testid={`sortable-row-${id}`}
            className={`flex items-center gap-3 rounded-sm border ${isDragging ? "border-accent-red bg-accent-red/5" : "border-border"} bg-card p-3`}
        >
            <button
                type="button"
                aria-label="Drag to reorder — tap and hold"
                data-testid={`sortable-grip-${id}`}
                {...attributes}
                {...listeners}
                className="touch-none flex-none cursor-grab rounded-sm border border-border bg-secondary/30 p-1.5 text-muted-foreground hover:bg-secondary active:cursor-grabbing"
                style={{ touchAction: "none" }}
            >
                <GripVertical className="h-4 w-4" />
            </button>
            <div className="flex-1 min-w-0">{children}</div>
        </div>
    );
}
